import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PlanFormModal = ({ isOpen, onClose, plan, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    monthlyPrice: '',
    yearlyPrice: '',
    status: 'active',
    features: {
      bmc: false,
      predictiveAnalysis: false,
      goalIntelligence: false,
      decisionIntelligence: false,
      reports: false
    },
    limits: {
      reportDownloads: '',
      aiUsagePerMonth: ''
    }
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan?.name || '',
        description: plan?.description || '',
        monthlyPrice: plan?.monthlyPrice?.toString() || '',
        yearlyPrice: plan?.yearlyPrice?.toString() || '',
        status: plan?.status || 'active',
        features: plan?.features || {
          bmc: false,
          predictiveAnalysis: false,
          goalIntelligence: false,
          decisionIntelligence: false,
          reports: false
        },
        limits: {
          reportDownloads: plan?.limits?.reportDownloads === -1 ? '-1' : plan?.limits?.reportDownloads?.toString() || '',
          aiUsagePerMonth: plan?.limits?.aiUsagePerMonth === -1 ? '-1' : plan?.limits?.aiUsagePerMonth?.toString() || ''
        }
      });
    } else {
      setFormData({
        name: '',
        description: '',
        monthlyPrice: '',
        yearlyPrice: '',
        status: 'active',
        features: {
          bmc: false,
          predictiveAnalysis: false,
          goalIntelligence: false,
          decisionIntelligence: false,
          reports: false
        },
        limits: {
          reportDownloads: '',
          aiUsagePerMonth: ''
        }
      });
    }
    setErrors({});
  }, [plan, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Plan name is required';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData?.monthlyPrice === '' || isNaN(formData?.monthlyPrice) || Number(formData?.monthlyPrice) < 0) {
      newErrors.monthlyPrice = 'Valid monthly price is required';
    }

    if (formData?.yearlyPrice === '' || isNaN(formData?.yearlyPrice) || Number(formData?.yearlyPrice) < 0) {
      newErrors.yearlyPrice = 'Valid yearly price is required';
    }

    if (formData?.limits?.reportDownloads === '' || (isNaN(formData?.limits?.reportDownloads) || (Number(formData?.limits?.reportDownloads) < -1))) {
      newErrors.reportDownloads = 'Valid limit required (-1 for unlimited)';
    }

    if (formData?.limits?.aiUsagePerMonth === '' || (isNaN(formData?.limits?.aiUsagePerMonth) || (Number(formData?.limits?.aiUsagePerMonth) < -1))) {
      newErrors.aiUsagePerMonth = 'Valid limit required (-1 for unlimited)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    setTimeout(() => {
      const planData = {
        name: formData?.name?.trim(),
        description: formData?.description?.trim(),
        monthlyPrice: Number(formData?.monthlyPrice),
        yearlyPrice: Number(formData?.yearlyPrice),
        status: formData?.status,
        features: formData?.features,
        limits: {
          reportDownloads: Number(formData?.limits?.reportDownloads),
          aiUsagePerMonth: Number(formData?.limits?.aiUsagePerMonth)
        }
      };

      onSave(planData);
      setIsProcessing(false);
    }, 500);
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const featuresList = [
    { key: 'bmc', label: 'Business Model Canvas', description: 'Access to BMC module' },
    { key: 'predictiveAnalysis', label: 'Predictive Analysis', description: 'Revenue forecasting and predictions' },
    { key: 'goalIntelligence', label: 'Goal Intelligence', description: 'Goal setting and tracking' },
    { key: 'decisionIntelligence', label: 'Decision Intelligence', description: 'AI-powered decision support' },
    { key: 'reports', label: 'Reports & Analytics', description: 'Advanced reporting features' }
  ];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-3xl bg-card rounded-xl elevation-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              {plan ? 'Edit Plan' : 'Create New Plan'}
            </h2>
            <p className="text-sm text-muted-foreground font-body mt-1">
              Configure pricing, features, and usage limits
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="p-2 rounded-md hover:bg-muted transition-smooth disabled:opacity-50"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Basic Information
            </h3>
            
            <Input
              label="Plan Name"
              placeholder="e.g., Growth, Scale, Enterprise"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              error={errors?.name}
              required
              disabled={isProcessing}
            />

            <Input
              label="Description"
              placeholder="Brief description of the plan"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              error={errors?.description}
              required
              disabled={isProcessing}
            />

            <Select
              label="Status"
              value={formData?.status}
              onChange={(value) => setFormData({ ...formData, status: value })}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              disabled={isProcessing}
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Pricing
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Monthly Price (₹)"
                type="number"
                placeholder="0"
                value={formData?.monthlyPrice}
                onChange={(e) => setFormData({ ...formData, monthlyPrice: e?.target?.value })}
                error={errors?.monthlyPrice}
                required
                disabled={isProcessing}
              />

              <Input
                label="Yearly Price (₹)"
                type="number"
                placeholder="0"
                value={formData?.yearlyPrice}
                onChange={(e) => setFormData({ ...formData, yearlyPrice: e?.target?.value })}
                error={errors?.yearlyPrice}
                required
                disabled={isProcessing}
              />
            </div>

            {formData?.monthlyPrice > 0 && formData?.yearlyPrice > 0 && (
              <div className="p-3 bg-accent/10 border border-accent/20 rounded-md">
                <p className="text-sm text-foreground font-body">
                  Yearly savings: <span className="font-semibold">
                    {Math.round((1 - (Number(formData?.yearlyPrice) / (Number(formData?.monthlyPrice) * 12))) * 100)}%
                  </span>
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Features
            </h3>
            
            <div className="space-y-3">
              {featuresList?.map((feature) => (
                <Checkbox
                  key={feature?.key}
                  label={feature?.label}
                  description={feature?.description}
                  checked={formData?.features?.[feature?.key]}
                  onChange={(e) => setFormData({
                    ...formData,
                    features: {
                      ...formData?.features,
                      [feature?.key]: e?.target?.checked
                    }
                  })}
                  disabled={isProcessing}
                />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Usage Limits
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Report Downloads per Month"
                type="number"
                placeholder="-1 for unlimited"
                value={formData?.limits?.reportDownloads}
                onChange={(e) => setFormData({
                  ...formData,
                  limits: { ...formData?.limits, reportDownloads: e?.target?.value }
                })}
                error={errors?.reportDownloads}
                description="Use -1 for unlimited"
                required
                disabled={isProcessing}
              />

              <Input
                label="AI Usage per Month"
                type="number"
                placeholder="-1 for unlimited"
                value={formData?.limits?.aiUsagePerMonth}
                onChange={(e) => setFormData({
                  ...formData,
                  limits: { ...formData?.limits, aiUsagePerMonth: e?.target?.value }
                })}
                error={errors?.aiUsagePerMonth}
                description="Use -1 for unlimited"
                required
                disabled={isProcessing}
              />
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={handleClose}
              disabled={isProcessing}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isProcessing}
              iconName="Save"
              iconPosition="left"
              fullWidth
            >
              {isProcessing ? 'Saving...' : (plan ? 'Update Plan' : 'Create Plan')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanFormModal;